import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { ProfilePictureStep, ProfileReadOnlyComponent } from '../../steps'
import {
	type ModalContextProps,
	StepModal,
	useModalContext,
	withStepModalProvider,
} from '@adsk/napi-shared'
import {
	NapiClient,
	OnboardedBuilderNetworkResponse,
} from '@adsk/napi-api-client'
import withConfig, { type Config } from '@adsk/napi-config'
import {
	OnboardingUserAppContextType,
	OnboardingUserAppProvider,
	useOnboardingUserContext,
} from '../../store'
import withBugsnag, { BugsnagNotify } from '@adsk/napi-bugsnag'
import {
	showLoadingState,
	setFlowType,
	setAppConfig,
	setUserInfo,
	setSelectionsList,
	setImmutableUserInfo,
} from '../../store/actions'
import {
	withMultipleProviders,
	ConditionalErrorBoundary,
} from '@adsk/napi-shared'
import { track } from '@adsk/acs-web-analytics'
import ANALYTICS_TYPES from '../../types/AnalyticsTypes'
import { VIEW_EDIT_PROFILE_FLOW } from '../../types/ActionTypes'
import { getPackageInfo } from '../../utils'
import { eliminateDefaultXAvatar, useAsyncError } from '../../utils/helpers'
import { SELECTION_LIST_KEYS } from '../../constants'
import {
	type FeatureFlagContextType,
	FeatureFlagProvider,
	useFeatureFlags,
} from '@adsk/napi-feature-flags'
import { useExoTrackReadyPagePerformance } from '@adsk/napi-exo-client'
import { ErrorModal } from '../../components/error-modal/ErrorModalComponent'
const { version, name } = getPackageInfo()
const displayName = 'NAPIC - View/Edit User Modal'

interface EditUserProfileModalAppProps {
	config: Config
}
const EditUserProfileModalApp: React.FC<EditUserProfileModalAppProps> = ({
	config,
}: EditUserProfileModalAppProps) => {
	const {
		dispatch,
		externalProps,
		internalProps,
		state: { showLoader },
	} = useOnboardingUserContext() as OnboardingUserAppContextType

	const throwError = useAsyncError()
	const { openModal, closeModal } = useModalContext() as ModalContextProps

	if (externalProps?.showModal) {
		openModal()
	}

	const {
		state: { napiUserOnboarding = false, featureFlagFetchError = false },
	} = useFeatureFlags() as unknown as FeatureFlagContextType

	useExoTrackReadyPagePerformance({
		displayName,
		isLoading: napiUserOnboarding && showLoader,
	})

	const getUserMeProfileInfoHandler = useCallback(() => {
		NapiClient.getUserMeProfileInfo()
			.then((response: OnboardedBuilderNetworkResponse) => {
				if (response.isOnboarded) {
					dispatch(showLoadingState(true))
					getSelectionsListJobTitleandCompanyType()
					response.avatarUrl = eliminateDefaultXAvatar(response.avatarUrl)
					if (!response?.bcUserId) {
						track(ANALYTICS_TYPES.NAPI_USER_IN_ACC_WITHOUT_BC_USER_ID, {
							package: name,
							component: BuildersProfileModal.name,
							version,
							flowType: VIEW_EDIT_PROFILE_FLOW,
						})
					}
					const userResponse = {
						...response,
						// Added trim() - coverage for spaces in the beginning and end of the string
						title: response?.title?.trim(),
						updatedAvatarUrl: response?.avatarUrl as string,
						phone: response?.phone ?? '',
					}
					dispatch(setUserInfo(userResponse))
					dispatch(setImmutableUserInfo(userResponse))
					dispatch(showLoadingState(false))
				} else {
					// user is not onboarded to builders network, so close the state
					closeModal()
				}
			})
			.catch((error: Error) => {
				// Catch errors to prevent blocking user flow, but do notify bugsnag
				BugsnagNotify(error, {
					message:
						'Network UserProfileMe API Error on BuildersProfile Modal application',
				})
				if (externalProps?.showModal) {
					closeModal()
					internalProps?.setIsFetchFailure(true)
					throwError(error)
				}
			})
	}, [dispatch, openModal])

	const getSelectionsListJobTitleandCompanyType = useCallback(() => {
		NapiClient.getSelectionListByName(
			`${SELECTION_LIST_KEYS.JOB_TITLES},${SELECTION_LIST_KEYS.COMPANY_TYPES}`
		)
			.then((response) => {
				dispatch(setSelectionsList(response))
			})
			.catch((error: Error) => {
				BugsnagNotify(error)
			})
	}, [])

	useEffect(() => {
		if (napiUserOnboarding && externalProps?.showModal) {
			dispatch(setFlowType(VIEW_EDIT_PROFILE_FLOW))
			dispatch(setAppConfig(config))
			track(ANALYTICS_TYPES.NAPI_BUILDERS_PROFILE_MODAL_MOUNT, {
				package: name,
				component: EditUserProfileModalApp.name,
				version,
				profileMode: 'view',
			})
			getUserMeProfileInfoHandler()
			return () => {
				track(ANALYTICS_TYPES.NAPI_BUILDERS_PROFILE_MODAL_UNMOUNT, {
					package: name,
					version,
				})
			}
		} else if (featureFlagFetchError && externalProps?.showModal) {
			closeModal()
			internalProps?.setIsFetchFailure(true)
			throwError(new Error('Feature flag fetch error'))
		}
	}, [napiUserOnboarding, featureFlagFetchError, externalProps, config])

	// Enable the edit profile link in the header if the user is eligible to view/edit the profile
	useEffect(() => {
		if (napiUserOnboarding) {
			externalProps?.setIsBuildersProfileModalEnabled?.(true)
		}
	}, [napiUserOnboarding, externalProps])

	return (
		<StepModal
			steps={[ProfileReadOnlyComponent(), ProfilePictureStep()]}
			styles={{
				overlayStyle: {
					// Allowing the modal to be on top of the other elements
					zIndex: 1000000,
				},
			}}
		/>
	)
}

const BuildersProfileModal = ({
	config,
	showModal,
	setShowModal,
	setIsBuildersProfileModalEnabled,
}: {
	config: Config
	showModal: boolean
	setShowModal: React.Dispatch<React.SetStateAction<boolean>>
	setIsBuildersProfileModalEnabled?: React.Dispatch<
		React.SetStateAction<boolean | undefined>
	>
}): JSX.Element | null => {
	const [isFetchFailure, setIsFetchFailure] = useState<boolean>(false)
	const EditUserProfileModalAppWithProviders = useMemo(
		() =>
			withMultipleProviders(EditUserProfileModalApp, [
				{
					provider: OnboardingUserAppProvider,
					props: {
						// Props to communicate between the Parent of BuildersProfileModal (e.g. exo) and the EditUserProfileModalApp
						externalProps: {
							setShowModal,
							showModal,
							setIsBuildersProfileModalEnabled,
						},
						// Props to communicate between BuildersProfileModal and EditUserProfileModalApp
						internalProps: {
							setIsFetchFailure,
						},
					},
				},
				{
					provider: FeatureFlagProvider,
					props: {
						queryParams: {
							appType: 'viewEdit',
							flags: 'napiUserOnboarding',
						},
					},
				},
			]),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[showModal]
	)

	return (
		<ConditionalErrorBoundary
			componentName={name}
			componentVersion={version}
			isFetchFailure={isFetchFailure}
			ErrorModal={ErrorModal}
		>
			<EditUserProfileModalAppWithProviders config={config} />
		</ConditionalErrorBoundary>
	)
}

export default withConfig(
	withStepModalProvider(withBugsnag(BuildersProfileModal, displayName, version))
)
